import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import './TaskMainInfo.scss'
import SectionItem from '@components/Layout/SectionItem/SectionItem'
import ProjectTitle from '@/app/components/ActivityCreation/MainInfo/ProjectTitle/ProjectTitle'
import AddAttachment from '@components/UI/AddAttachment/AddAttachment'
import MessagingOptions from '@/app/components/ActivityCreation/MainInfo/MessagingOptions/MessagingOptions'
import { LuxRichInputText, LuxSelect } from '@lux/wardrobe-lib'
import SectionItemContainer from '@components/Layout/SectionItemContainer/SectionItemContainer'
import { NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE } from '@/app/redux/reducers/activity-creation'
import { ActivitySection, NewActivitySectionType } from '@/app/models/new-activity-section-type'
import { setActivitySectionStatus } from '@/app/redux/actions/activity-creation'
import { ActivityStep } from '@/app/models/new-activity-creation-state'
import { FormBase, MainInfoForm } from '@/app/models/new-activity-form'
import { taskCommunicationForm } from '@models/new-activity-task-communication-form'
import { useEffect } from 'react'
import { ProjectsService } from '@/app/services/ProjectsService'
import {
    SmartlyActionSubtypes,
    Analytics,
    SmartlyActionItems,
} from '@Techedge/digitalportal-shared'
import PhTaskType from '../TaskType/PhTaskType'

const analyticsPageTypeMap = {
    communication: 'Task:Communication:Creation',
    serviceRequest: 'Task:ServiceRequest:Creation',
    manainancePhoto: 'Task:MantainancePhoto:Creation',
    highQualityPhoto: 'Task:HighQualityPhoto:Creation',
}

const TaskMainInfo = (props: FormBase<MainInfoForm>) => {
    const componentClass = 'smart-new-activity-task-main-info'
    const intl = useIntl()
    const {
        form,
        taskTypeform,
        activityTypeList,
        onSetForm,
        onTaskTypeChange,
        onSave,
        isDone,
        setActivitySectionStatus,
    } = props

    useEffect(() => {
        setActivitySectionStatus(ActivitySection.TASK_ACTIVITY, NewActivitySectionType.ACTION, {
            isHide: form.confirmRead.value,
        })
    }, [form.confirmRead])

    const onUpload = (files) => {
        onSetForm({
            ...form,
            attachmentList: {
                value: [
                    ...form.attachmentList.value,
                    ...files.map((file: any) => ({
                        fileBlob: file.file,
                        file: file.file,
                        url: file.url,
                        id: file.id,
                    })),
                ],
            },
        })
    }

    const onRemoveAttachment = (file) => {
        let newList = [...form.attachmentList.value]
        const fileIndex = newList.findIndex((i) => i.file.name === file.name)
        newList.splice(fileIndex, 1)
        onSetForm({
            ...form,
            attachmentList: {
                value: [...newList],
            },
        })
    }

    return (
        <SectionItemContainer
            hideFooter={true}
            onSave={() => onSave()}
            isDone={isDone}
            onClear={() =>
                onSetForm({ ...taskCommunicationForm[NewActivitySectionType.MAIN_INFO] })
            }>
            <SectionItem>
                <LuxSelect
                    value={taskTypeform.type.value}
                    onChangeCallback={(value) => {
                        let label = activityTypeList
                            .find((a) => a.key === SmartlyActionItems.TASK)
                            ?.subTypes.find((t) => t.value === value)?.label
                        Analytics._sendAnalyticsLinkEvent(
                            'Activities',
                            analyticsPageTypeMap[value],
                            'Static'
                        )
                        onTaskTypeChange({ type: { ...form.type, value, label } })
                    }}
                    mandatory={taskTypeform.type.mandatory}
                    disabled={taskTypeform.type.disabled}
                    label={intl.formatMessage({
                        id: 'ph2.modals.new.activity.task.type.select',
                        defaultMessage: 'Select task type',
                    })}
                    options={activityTypeList
                        .find((a) => a.key === SmartlyActionItems.TASK)
                        ?.subTypes.map((type) => ({
                            ...type,
                            label: intl.formatMessage({
                                id: `ph2.modals.new.activity.type.${type.value}`,
                                defaultMessage: type.value,
                            }),
                        }))}
                    placeholder={intl.formatMessage({
                        id: 'modals.new.activity.task.type.select.placeholder',
                        defaultMessage: 'Select news type',
                    })}
                    additionalProps={{
                        appendTo: 'self',
                    }}
                />
                {taskTypeform.type.value == SmartlyActionSubtypes.PICTURE_MGMT && (
                    <PhTaskType
                        form={taskTypeform}
                        onSetForm={(form) => onTaskTypeChange(form)}
                        typeList={
                            activityTypeList
                                .find((a) => a.key === SmartlyActionItems.TASK)
                                ?.subTypes.find(
                                    (t) => t.value == SmartlyActionSubtypes.PICTURE_MGMT
                                )?.children
                        }
                    />
                )}
            </SectionItem>
            <SectionItem>
                <ProjectTitle form={form} onSetForm={(form) => onSetForm(form)} />
            </SectionItem>
            <div className={`${componentClass}__field-container`}>
                <LuxRichInputText
                    value={form.info.value}
                    onChangeCallback={(value: string) => {
                        onSetForm({ ...form, info: { value } })
                    }}
                    placeholder={intl.formatMessage({
                        id: 'modals.new.activity.section.main.info.rich.text.input.placeholder',
                        defaultMessage: 'Type here',
                    })}
                    label={intl.formatMessage({
                        id: 'modals.new.activity.section.main.info.rich.text.input.label',
                        defaultMessage: 'Info',
                    })}
                    config={{
                        events: {
                            initialized() {
                                const editor = this
                                if (form.info.disabled) editor.edit.off()
                            },
                        },
                    }}
                    fileUploadUrl={ProjectsService.FROALA_UPLOAD_URL}
                />
            </div>
            <div className={`${componentClass}__field-container`}>
                <AddAttachment
                    form={form}
                    onUpload={(form) => onUpload(form)}
                    showRemoveIcon
                    onRemove={(file) => onRemoveAttachment(file)}
                    showSplitFile
                    disabled={form.attachmentList.disabled}
                />
            </div>
            <MessagingOptions form={form} onSetForm={(form) => onSetForm(form)} />
        </SectionItemContainer>
    )
}

const mapStateToProps = (state: any) => ({
    sectionMap: state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE].taskActivity.sectionMap,
    taskTypeform:
        state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE][ActivitySection.TASK_ACTIVITY].form[
            NewActivitySectionType.TASK_TYPE
        ],
})

const mapDispatchToProps = (dispatch: any) => ({
    setActivitySectionStatus: (
        activityKey: string,
        sectionKey: NewActivitySectionType,
        section: Partial<ActivityStep>
    ) => dispatch(setActivitySectionStatus(activityKey, sectionKey, section)),
})

export default connect(mapStateToProps, mapDispatchToProps)(TaskMainInfo)
